import { useState, useEffect } from "react";
import { Card } from "./ui/card";
import { useScrollReveal } from "../hooks/use-scroll-reveal";
import { Star, Quote } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

// Fallback testimonials
const fallbackTestimonials = [
  {
    id: "1",
    name: "Michael Rodriguez",
    role: "Homeowner",
    company: "",
    quote: "Amazing work on our entire home interior! The team was professional, clean, and the finish is absolutely flawless. Highly recommend for any painting project.",
    rating: 5,
    project: "Whole Home Interior",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
    alt: "Happy customer of interior painters near Orlando Florida residential whole home painting project",
  },
  {
    id: "2",
    name: "Sarah Thompson",
    role: "Homeowner",
    company: "",
    quote: "They removed our popcorn ceilings and the difference is incredible! Our home looks so much more modern now. Worth every penny.",
    rating: 5,
    project: "Popcorn Ceiling Removal",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop",
    alt: "Satisfied customer of interior house painters in Orlando Florida popcorn ceiling removal",
  },
  {
    id: "3",
    name: "David Martinez",
    role: "Homeowner",
    company: "",
    quote: "Our exterior paint job looks fantastic! They helped us choose the perfect colors and the workmanship is top quality. Best painters in Orlando!",
    rating: 5,
    project: "Exterior House Painting",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop",
    alt: "Happy homeowner using exterior painting companies in Longwood Florida for house painting",
  },
  {
    id: "4",
    name: "Jennifer Walsh",
    role: "Homeowner",
    company: "",
    quote: "The garage floor coating exceeded our expectations. It looks like a showroom floor now. Professional team that really cares about quality.",
    rating: 5,
    project: "Garage Floor Epoxy",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop",
    alt: "Satisfied client of painting services in Casselberry Florida professional garage floor coating",
  },
];

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [loading, setLoading] = useState(false);
  const [gridRef, gridVisible] = useScrollReveal();

  useEffect(() => {
    const fetchTestimonials = async () => {
      if (!API) return; // No backend, use fallback data already set
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (response.data.testimonials?.length) {
          setTestimonials(response.data.testimonials);
        }
      } catch (error) {
        // Keep using fallback data
        console.log("Using fallback testimonials data");
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section
      data-testid="testimonials-section"
      className="py-20 sm:py-28 bg-brand-ice relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03]">
        <Quote className="w-full h-full text-brand-navy" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <div className="text-center mb-12 sm:mb-14">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
            Client Success Stories
          </span>
          <h2
            data-testid="testimonials-heading"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
          >
            What Our Clients Say
          </h2>
          <p className="font-body text-sm sm:text-base text-brand-slate mt-3 max-w-xl mx-auto">
            Hear from satisfied clients about their experience working with us
          </p>
        </div>

        <div
          ref={gridRef}
          className={`grid sm:grid-cols-2 gap-4 sm:gap-5 reveal-stagger ${gridVisible ? 'visible' : ''}`}
        >
          {testimonials.slice(0, 4).map((testimonial) => (
            <Card
              key={testimonial.id}
              data-testid={`testimonial-card-${testimonial.id}`}
              className="h-full bg-white border border-border/60 p-6 rounded-xl hover:border-brand-orange/30 card-lift"
            >
              <Quote className="w-8 h-8 text-brand-orange/20 mb-3" />

              <div className="flex gap-0.5 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={`${testimonial.id}-star-${i}`} className="w-4 h-4 fill-brand-orange text-brand-orange" />
                ))}
              </div>

              <blockquote className="font-body text-sm sm:text-base text-brand-slate leading-relaxed mb-5">
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>

              <div className="inline-block bg-brand-ice text-brand-navy text-[11px] font-medium uppercase tracking-wide px-2.5 py-1 rounded mb-5">
                {testimonial.project}
              </div>

              <div className="flex items-center gap-3 pt-5 border-t border-border/40">
                <img
                  src={testimonial.image}
                  alt={testimonial.alt || `${testimonial.name} - satisfied Roll Orlando painting customer`}
                  className="w-11 h-11 rounded-full object-cover border-2 border-brand-orange/15"
                />
                <div>
                  <h4 className="font-heading text-base font-semibold text-brand-navy">
                    {testimonial.name}
                  </h4>
                  <p className="text-xs text-brand-slate">
                    {testimonial.role}
                    {testimonial.company && `, ${testimonial.company}`}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
